import React from 'react';
import { useTranslation } from 'react-i18next';

// GUNDI-5665: the ONE shell every tutorial message renders through — card
// styling, progress, and the advance button all live here so a change lands on
// every step at once. Forward-only by product call: no back, no exit.
//
// No caret on purpose: the card sits in a FIXED viewport slot per tour zone
// (so the Next button never chases the target), and the spotlight alone does
// the pointing. A fixed card cannot carry a fixed-offset caret honestly.
//
// TEMPORARY (≤3 weeks) — see the removal manifest in tutorialInterceptor.ts.
type TutorialStepProps = {
  message: string;
  stepNumber: number; // 1-based
  totalSteps: number;
  onNext: () => void;
  isLast: boolean;
  style?: React.CSSProperties; // the fixed slot, computed by the overlay
};

export default function TutorialStep({
  message,
  stepNumber,
  totalSteps,
  onNext,
  isLast,
  style,
}: TutorialStepProps) {
  const { t } = useTranslation();
  return (
    <div
      data-testid="TutorialStep-card"
      role="dialog"
      aria-label={t('Gundi tutorial')}
      aria-live="polite"
      style={style}
      // FIXED height, not min-h: the card sits in a fixed slot, so any
      // height change from copy length would move the Next button (±16px
      // measured). 216px fits the longest message at five 15px lines.
      className="absolute z-[70] flex h-[216px] w-[340px] flex-col justify-between gap-3 rounded-xl border border-unlisted-gray-lines bg-white p-5 shadow-tutorial-step"
    >
      {/* 15px, one up from the app's text-sm (product call: readability
                over the dimmed page); the button label matches below. */}
      <p className="text-[15px] font-normal leading-relaxed text-ui-text-primary-text">
        {message}
      </p>
      <div className="flex flex-col gap-3">
        {/* A slim bar, not per-step dots: fourteen dots plus the count
                    overflowed the footer row and wrapped the counter, which
                    also moved the Next button. */}
        <div
          aria-hidden="true"
          className="h-1 w-full rounded-full bg-unlisted-gray-lines"
        >
          <div
            className="h-1 rounded-full bg-brand-accent-green transition-all duration-300"
            style={{ width: `${Math.round((stepNumber / totalSteps) * 100)}%` }}
          />
        </div>
        <div className="flex items-center justify-between">
          <span
            data-testid="TutorialStep-counter"
            className="whitespace-nowrap text-xs font-normal tabular-nums text-ui-text-primary-text"
          >
            {stepNumber} / {totalSteps}
          </span>
          <button
            type="button"
            data-testid="TutorialStep-next-button"
            onClick={onNext}
            aria-label={isLast ? t('Finish tutorial') : t('Next')}
            className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-md bg-brand-accent-green px-3.5 py-1.5 text-[15px] font-semibold text-white transition-colors duration-300 hover:bg-ui-cta-primary"
          >
            {isLast ? t('Finish') : t('Next')}
            {!isLast && (
              <svg
                aria-hidden="true"
                className="h-3.5 w-3.5"
                viewBox="0 0 20 20"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M4 10h11M11 5l5 5-5 5" />
              </svg>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
